import React, { useState } from 'react';
import delve from 'dlv';

import getSortedFieldGroups from '../shared/getSortedFieldGroups';
import getSortedGroupsWithValues from '../shared/getSortedGroupsWithValues';

import Box from 'components/elements/Box';

import {
  TableRow,
  Cell,
  CellContent,
  StyledEntityItem,
  ChangeArrow,
  StyledCaret,
  StyledCaretWrap,
  Metric,
  MetaDetails,
  MetricsList,
  ExpandedMetrics,
  MetricBox,
} from './BrowseTableRow.style';

import { entityTypeToApiType } from 'helpers/mapEntityTypes';
import { EntityTypes } from 'context/entityContext';
import { IMetric } from 'maps/metrics';
import { IInnerEntity, TableHeader } from '../shared/types';

interface IBrowseTableRowProps {
  browseEntityType: string;
  entity: IInnerEntity;
  isHighlighted?: boolean;
  tableHeaders: TableHeader[];
  tableName: string;
  sortBy: IMetric;
  releaseCompilation?: string;
}

const BrowseTableRow: React.FC<IBrowseTableRowProps> = (props) => {
  const {
    browseEntityType,
    entity,
    isHighlighted = false,
    tableHeaders,
    tableName,
    sortBy,
    releaseCompilation,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const isTopList = tableName === 'TopLists';

  const entityInfo = delve(
    entity,
    tableName === 'customLists' || tableName === 'trackCustomLists'
      ? 'entity'
      : (entityTypeToApiType(browseEntityType as EntityTypes) as string)
  );
  const entityData = browseEntityType === 'label' ? entityInfo : entity.data;
  const getChangeArrow = (isPositive: boolean | null): any =>
    isPositive === null ? ' ' : isPositive ? '▲' : '▼';
  const sortedFieldGroups = getSortedFieldGroups(tableHeaders);
  const sortedGroupsWithValues = getSortedGroupsWithValues(
    sortedFieldGroups,
    entity.data,
    isTopList
  );

  const isPositiveFieldGroup = sortedGroupsWithValues.reduce(
    (prev: any[], group: any) => {
      let sortedField;
      if (sortBy && sortBy.key !== 'performance_rtd') {
        const sortedGroup = group.some((item: any) => sortBy.key === item.key);
        sortedField =
          sortedGroup && group.filter((item: any) => item.label === 'Change');
      } else {
        sortedField = group.filter(
          (item: any) => sortBy && sortBy.key === item.key
        );
      }
      return sortedField.length > 0 ? [...prev, ...sortedField] : prev;
    },
    []
  );

  const isPositiveGroup =
    isPositiveFieldGroup.length > 0
      ? Number(isPositiveFieldGroup[0].rawValue) > 0
      : null;

  return (
    <TableRow
      isExpanded={isExpanded}
      onClick={(): void => setIsExpanded(!isExpanded)}
      isTopList={isTopList}
    >
      {!isTopList && tableName !== 'trackCustomLists' && (
        <Cell>
          <ChangeArrow
            isStretched={true}
            isExpanded={isExpanded}
            isPositive={isPositiveGroup}
          >
            {getChangeArrow(isPositiveGroup)}
          </ChangeArrow>
        </Cell>
      )}

      <CellContent>
        <StyledEntityItem
          entityId={entityInfo.id}
          entityType={entityInfo.type}
          displayName={entityInfo.displayName}
          primaryArtist={entityInfo.primaryArtist}
          thumbnailUrl={entityInfo.thumbnailUrl}
          releaseDate={entityInfo.releaseDate}
          isGroup={entityInfo.is_group}
          parentEntityId={entityInfo.parent_group_id}
          groupChildCount={entityInfo.group_child_count}
          entityData={entityData}
          releaseCount={entityInfo.grouped_album_count}
          artistCount={entityInfo.artist_count}
          trackCount={entityInfo.grouped_track_count}
          preserveSearch={true}
          isHighlighted={isHighlighted}
          isLink={isExpanded}
          isDisplayed={!isExpanded && !isTopList}
          sortBy={sortBy}
          tableName={tableName}
          releaseType={entity?.album?.productType}
          isrc_list={entityInfo.isrc_list}
          isTopList={isTopList}
          releaseCompilation={releaseCompilation}
        />

        {isExpanded && (
          <ExpandedMetrics>
            {sortedGroupsWithValues.map((group: any, index: number) => {
              const hasChangeItem =
                group.length > 0 &&
                group.filter((item: any) => 'isPositive' in item);
              const isPositive =
                hasChangeItem.length > 0 ? hasChangeItem[0].isPositive : null;

              return (
                group.length > 0 && (
                  <Box display="flex" alignItems="center" key={index}>
                    <Box
                      display="flex"
                      flex="0 1 48px"
                      justifyContent="flex-end"
                      pr={2}
                    >
                      <ChangeArrow isPositive={isPositive}>
                        {getChangeArrow(isPositive)}
                      </ChangeArrow>
                    </Box>

                    <MetricsList key={index}>
                      {group.map((item: any) => {
                        const isChangeField = item.label === 'Change';
                        const hasChangeColor = isChangeField
                          ? isPositive
                            ? 'PositiveChange'
                            : 'NegativeChange'
                          : 'ShadowBlack';
                        return (
                          <li key={item.key + item.value}>
                            <MetricBox isTopList={isTopList}>
                              <MetaDetails isUpperCase={true}>
                                {item.label}
                              </MetaDetails>
                              <Metric hasColor={hasChangeColor}>
                                {item.value}
                              </Metric>
                            </MetricBox>
                          </li>
                        );
                      })}
                    </MetricsList>
                  </Box>
                )
              );
            })}
          </ExpandedMetrics>
        )}
      </CellContent>

      <Cell open={isExpanded}>
        <StyledCaretWrap>
          <StyledCaret
            open={isExpanded}
            onClick={(): void => setIsExpanded(!isExpanded)}
          />
        </StyledCaretWrap>
      </Cell>
    </TableRow>
  );
};

export default BrowseTableRow;
